export interface TestimonialItem {
  quote: string;
  author: string;
  location?: string;
  rating?: number;
}

export interface PageTestimonialsProps {
  heading?: string;
  subheading?: string;
  items: TestimonialItem[];
}

export default function PageTestimonials({
  heading = 'What Our Customers Say',
  subheading,
  items,
}: PageTestimonialsProps) {
  return (
    <section className="bg-inkblack py-24 lg:py-32">
      <div className="max-w-screen-xl mx-auto px-8 lg:px-12">
        {/* Header row */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-6">
          <div>
            {subheading && (
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-antique" />
                <span className="text-antique text-xs font-sans font-500 tracking-[0.24em] uppercase">{subheading}</span>
              </div>
            )}
            <h2
              className="font-serif text-ivory"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' }}
            >
              {heading}
            </h2>
          </div>
          {/* Stats */}
          <div className="flex items-center gap-8">
            <div className="text-center">
              <p className="font-serif text-antique leading-none mb-1" style={{ fontSize: '2.5rem', fontWeight: 700 }}>5★</p>
              <p className="text-ivory/35 text-xs font-sans tracking-wide">Avg Rating</p>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div className="text-center">
              <p className="font-serif text-ivory leading-none mb-1" style={{ fontSize: '2.5rem', fontWeight: 700 }}>100%</p>
              <p className="text-ivory/35 text-xs font-sans tracking-wide">Recommend</p>
            </div>
          </div>
        </div>

        {/* Featured large quote — first testimonial */}
        {items[0] && (
          <div className="mb-14 pb-14 border-b border-white/10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-1 hidden lg:flex justify-center">
                <span
                  className="font-serif text-antique/20 leading-none select-none"
                  style={{ fontSize: '6rem', fontWeight: 700 }}
                  aria-hidden="true"
                >
                  &ldquo;
                </span>
              </div>
              <div className="lg:col-span-9">
                <div className="flex items-center gap-1 mb-5">
                  {[...Array(items[0].rating ?? 5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-antique text-base" />
                  ))}
                </div>
                <blockquote
                  className="font-serif text-ivory italic leading-relaxed mb-7"
                  style={{ fontSize: 'clamp(1.3rem, 2.5vw, 2rem)', fontWeight: 400, lineHeight: 1.55 }}
                >
                  &ldquo;{items[0].quote}&rdquo;
                </blockquote>
                <div className="flex items-center gap-4">
                  <span className="w-10 h-px bg-antique/50" />
                  <div>
                    <p className="text-ivory text-sm font-sans font-600 tracking-wide">{items[0].author}</p>
                    {items[0].location && (
                      <p className="text-pewter text-xs font-sans font-300 mt-0.5">{items[0].location}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-2" />
            </div>
          </div>
        )}

        {/* Remaining testimonials — static 2-col then 3-col card grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.slice(1).map((item, i) => (
            <div key={i} className="border border-white/10 p-6 bg-white/2">
              <div className="flex items-center gap-0.5 mb-4">
                {[...Array(item.rating ?? 5)].map((_, s) => (
                  <i key={s} className="ri-star-fill text-antique text-xs" />
                ))}
              </div>
              <p className="text-ivory/65 text-xs font-sans font-300 leading-relaxed mb-5 italic">
                &ldquo;{item.quote}&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-white/8">
                <span className="w-5 h-px bg-antique/40" />
                <div>
                  <p className="text-ivory text-xs font-sans font-600 tracking-wide">{item.author}</p>
                  {item.location && (
                    <p className="text-pewter text-[10px] font-sans font-300 mt-0.5">{item.location}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
